/**
 * sources/index.ts — F1d synchronous context source assembly.
 *
 * Builds the static/synchronous portions of EvalContext from all available
 * sources at session start. ANSWER and VARIABLE are populated reactively
 * by the player (handled in varCtx / variables state — not here).
 *
 * DOM reads (navigator.userAgent, window.location.search) are isolated
 * behind readDomSources() — callers pass the result in, enabling SSR safety
 * and clean test substitution.
 *
 * Pure-ish: buildSyncSources is pure given its params. readDomSources reads
 * browser globals but is fail-open (returns '' in SSR / jsdom).
 */

import { detectOs, detectBrowser } from './ua-detect';
import type { EvalContext } from '@/logic-engine';

export interface SyncSourceParams {
  /** window.location.search (or '' in SSR/jsdom). */
  search: string;
  /** navigator.userAgent (or '' in SSR/jsdom). */
  ua: string;
  /** ISO 3166-1 alpha-2 from quiz/paywall geo_country field. null = unknown. */
  geoCountry: string | null;
  /** F6 variable space for VARIABLE-source conditions. */
  variables: Record<string, string>;
}

/** Subset of EvalContext fields resolved synchronously. */
export type SyncSources = Pick<
  EvalContext,
  'urlParams' | 'geoCountry' | 'deviceOs' | 'browser' | 'browserInApp' | 'variables'
>;

/**
 * buildSyncSources — resolve all synchronous EvalContext sources.
 *
 * Fails open for every source:
 *   - Malformed search string → urlParams = {}
 *   - Empty UA → deviceOs = 'other', browser = 'other', browserInApp = false
 *   - Null geoCountry → geoCountry = null (GEO conditions evaluate FALSE in F1a)
 *
 * @param params  DOM-isolated inputs (see SyncSourceParams)
 * @returns SyncSources — ready to spread into EvalContext
 */
export function buildSyncSources(params: SyncSourceParams): SyncSources {
  const { search, ua, geoCountry, variables } = params;

  // URL_PARAM resolution
  const urlParams: Record<string, string> = {};
  try {
    if (search) {
      new URLSearchParams(search).forEach((v, k) => {
        urlParams[k] = v;
      });
    }
  } catch {
    // fail-open: malformed search string → empty urlParams
  }

  // DEVICE_OS + BROWSER + in_app resolution
  const deviceOs = detectOs(ua);
  const { browser, inApp: browserInApp } = detectBrowser(ua);

  return {
    urlParams,
    geoCountry,
    deviceOs,
    browser,
    browserInApp,
    variables,
  };
}

/**
 * readDomSources — reads browser globals for UA and URL.
 *
 * Isolated here so React components can call this once during init
 * and pass the result into buildSyncSources — easy to substitute in tests
 * by mocking navigator.userAgent / window.location.search before render.
 *
 * Returns empty strings in SSR / non-browser environments (fail-open).
 */
export function readDomSources(): { ua: string; search: string } {
  try {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const search =
      typeof window !== 'undefined' && window.location ? window.location.search : '';
    return { ua, search };
  } catch {
    return { ua: '', search: '' };
  }
}
